import React, { useMemo } from 'react';
import { Badge } from '@chakra-ui/react';
import { twMerge } from 'tailwind-merge';
import { FaThLarge, FaThList, FaPlus } from 'react-icons/fa';

import { Button, Input } from 'common/materials';
import SupportModal from 'common/components/TopNavigation/SupportModal';

import { Tabs, TicketCard, TicketDetailModal, TicketTable } from './components';
import useTicketViewModel from './TicketLegacy.viewmodel';

export const TABS = [
    {
        label: 'All',
        value: 'all',
        color: 'bg-primary-50 text-primary-600'
    },
    {
        label: 'Processing',
        value: 'กำลังดำเนินการ',
        color: 'bg-blue-50 text-blue-600'
    },
    {
        label: 'Cancel',
        value: 'ถูกยกเลิก',
        color: 'bg-red-50 text-red-600'
    },
    {
        label: 'Finish',
        value: 'ปิดงานแล้ว',
        color: 'bg-green-50 text-green-600'
    }
] as const;

function TicketLegacyView() {
    const {
        email,
        filter,
        displayStyle,
        tasks = [],
        filteredTasks,
        search,
        showSupportModal,
        selectedTask,
        selectedTaskDetail,
        showTaskDetailModal,
        setFilter,
        setDisplayStyle,
        handleChangeSearch,
        handleClickTicketDetail,
        handleCloseTicketDetailModal,
        handleClickCloseTask,
        handleClickCancelTask,
        setShowSupportModal,
        onClickReply
    } = useTicketViewModel();

    const statusCounts = useMemo(
        () =>
            tasks.reduce((accumulate, task) => {
                return accumulate.set(
                    task.activityOverviewStatus,
                    (accumulate.get(task.activityOverviewStatus) ?? 0) + 1
                );
            }, new Map<string, number>()),
        [tasks]
    );

    const tabs = useMemo(
        () =>
            [
                {
                    label: `All (${tasks.length})`,
                    value: 'all'
                },
                {
                    label: `Processing (${statusCounts.get('กำลังดำเนินการ') ?? 0})`,
                    value: 'กำลังดำเนินการ'
                },
                {
                    label: `Cancel (${statusCounts.get('ถูกยกเลิก') ?? 0})`,
                    value: 'ถูกยกเลิก'
                },
                {
                    label: `Finish (${statusCounts.get('ปิดงานแล้ว') ?? 0})`,
                    value: 'ปิดงานแล้ว'
                }
            ] as const,
        [tasks, statusCounts]
    );

    return (
        <React.Fragment>
            <div className="flex flex-1 flex-col gap-4">
                <div className="flex">
                    <p className="text-2xl font-bold">TASK TICKET</p>
                    <Button
                        className="ml-auto flex flex-row items-center gap-2 rounded-[6px] bg-primary-900 px-4 text-white"
                        onClick={setShowSupportModal.on}
                    >
                        <FaPlus size={10} />
                        <span className="text-sm">New Ticket</span>
                    </Button>
                </div>
                <div className="flex items-center justify-between">
                    <Badge
                        variant="subtle"
                        colorScheme="primary"
                        className="w-fit rounded-md px-2 py-1 text-xs"
                    >
                        {email}
                    </Badge>

                    <div className="relative flex w-fit overflow-hidden rounded-md border [&_>_button:not(:last-child)]:border-r">
                        <button
                            className={twMerge(
                                'flex flex-1 items-center justify-center px-3 text-stone-300 transition-colors',
                                displayStyle === 'grid' && 'bg-zinc-300 text-[#777777]'
                            )}
                            onClick={() => setDisplayStyle('grid')}
                        >
                            <FaThLarge />
                        </button>
                        <button
                            className={twMerge(
                                'flex flex-1 items-center justify-center px-3 py-2 text-stone-300 transition-colors',
                                displayStyle === 'list' && 'bg-zinc-300 text-[#777777]'
                            )}
                            onClick={() => setDisplayStyle('list')}
                        >
                            <FaThList />
                        </button>
                    </div>
                </div>

                <Tabs
                    tabs={tabs}
                    value={filter}
                    onClick={setFilter}
                />

                <Input
                    placeholder="Search ..."
                    value={search}
                    onChange={handleChangeSearch}
                />

                {displayStyle === 'grid' ? (
                    <div className="grid grid-cols-1 gap-4 overflow-hidden md:grid-cols-2">
                        {filteredTasks && filteredTasks.length > 0 ? (
                            filteredTasks.map((ticket, index) => (
                                <div key={index}>
                                    <TicketCard ticket={ticket} />
                                </div>
                            ))
                        ) : (
                            <div className="col-span-2 text-center">No ticket...</div>
                        )}
                    </div>
                ) : (
                    <TicketTable
                        tasks={filteredTasks}
                        onClickTicketDetail={handleClickTicketDetail}
                    />
                )}
            </div>

            {/* Create ticket modal */}
            <SupportModal
                header="Create Ticket"
                isOpen={showSupportModal}
                onClose={setShowSupportModal.off}
            />

            <TicketDetailModal
                isOpen={showTaskDetailModal}
                task={selectedTask}
                taskActivity={selectedTaskDetail}
                onClose={handleCloseTicketDetailModal}
                onClickReply={onClickReply}
                onClickCloseTask={handleClickCloseTask}
                onClickCancelTask={handleClickCancelTask}
            />
        </React.Fragment>
    );
}

export default TicketLegacyView;
